import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/config';
import { Button } from '../../components/admin/shared/Button';
import { Input } from '../../components/admin/shared/Input';
import { Textarea } from '../../components/admin/shared/Textarea';
import { Modal } from '../../components/admin/shared/Modal';
import { useToast, ToastContainer } from '../../components/admin/shared/Toast';
import { Plus, Edit, Trash2 } from 'lucide-react';

const Experience = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [formData, setFormData] = useState({
    type: 'work',
    title: '',
    organization: '',
    location: '',
    description: '',
    startDate: '',
    endDate: '',
    current: false,
    skillsUsed: ''
  });
  const { toasts, addToast } = useToast();

  const fetchExperiences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('experience')
        .select('*')
        .order('start_date', { ascending: false });

      if (error) throw error;
      setExperiences(data || []);
    } catch (error) {
      console.error('Error fetching experience:', error);
      addToast('Error loading experience', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchExperiences();
  }, [fetchExperiences]);

  const handleAdd = () => {
    setEditingExp(null);
    setFormData({
      type: 'work',
      title: '',
      organization: '',
      location: '',
      description: '',
      startDate: '',
      endDate: '',
      current: false,
      skillsUsed: ''
    });
    setShowModal(true);
  };

  const handleEdit = (exp) => {
    setEditingExp(exp);
    setFormData({
      type: exp.type || 'work',
      title: exp.title || '',
      organization: exp.organization || '',
      location: exp.location || '',
      description: exp.description || '',
      startDate: exp.start_date || '',
      endDate: exp.end_date || '',
      current: exp.current || false,
      skillsUsed: (exp.skills_used || []).join(', ')
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const data = {
        type: formData.type,
        title: formData.title,
        organization: formData.organization,
        location: formData.location,
        description: formData.description,
        start_date: formData.startDate,
        end_date: formData.current ? null : formData.endDate,
        current: formData.current,
        skills_used: formData.skillsUsed
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0)
      };

      if (editingExp) {
        const { error } = await supabase
          .from('experience')
          .update(data)
          .eq('id', editingExp.id);

        if (error) throw error;
        addToast('Experience updated successfully', 'success');
      } else {
        const { error } = await supabase
          .from('experience')
          .insert(data);

        if (error) throw error;
        addToast('Experience added successfully', 'success');
      }
      setShowModal(false);
      fetchExperiences();
    } catch (error) {
      console.error('Error saving experience:', error);
      addToast('Error saving experience', 'error');
    }
  };

  const handleDelete = async (expId) => {
    if (!window.confirm('Delete this experience?')) return;

    try {
      const { error } = await supabase
        .from('experience')
        .delete()
        .eq('id', expId);

      if (error) throw error;
      addToast('Experience deleted successfully', 'success');
      fetchExperiences();
    } catch (error) {
      console.error('Error deleting experience:', error);
      addToast('Error deleting experience', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div>
      <ToastContainer toasts={toasts} />
      
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Experience</h1>
        <Button
          variant="primary"
          onClick={handleAdd}
          className="flex items-center gap-2"
        >
          <Plus size={18} />
          Add Experience
        </Button>
      </div>

      {loading ? (
        <div className="text-white">Loading...</div>
      ) : experiences.length === 0 ? (
        <div className="bg-slate-800 rounded-lg p-12 text-center border border-slate-700">
          <p className="text-slate-400 mb-4">No experience entries yet.</p>
          <Button variant="primary" onClick={handleAdd}>
            Add Experience
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {experiences.map((exp) => (
            <div
              key={exp.id}
              className="bg-slate-800 rounded-lg p-6 border border-slate-700"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">
                    {exp.title}
                  </h3>
                  <p className="text-violet-400 font-medium mb-2">
                    {exp.organization}
                    {exp.location && ` • ${exp.location}`}
                  </p>
                  <p className="text-slate-400 text-sm mb-2">
                    {formatDate(exp.start_date)} - {exp.current ? 'Present' : formatDate(exp.end_date)}
                  </p>
                  {exp.description && (
                    <p className="text-slate-300 mb-2">{exp.description}</p>
                  )}
                  {exp.skills_used && exp.skills_used.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {exp.skills_used.map((skill, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEdit(exp)}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(exp.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingExp ? 'Edit Experience' : 'Add Experience'}
      >
        <div className="mb-4">
          <label className="block text-sm text-slate-400 mb-2">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-violet-500"
          >
            <option value="work">Work</option>
            <option value="education">Education</option>
            <option value="certification">Certification</option>
            <option value="award">Award</option>
          </select>
        </div>
        <Input
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Software Engineer"
        />
        <Input
          label="Organization"
          value={formData.organization}
          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
          placeholder="Company or Institution"
        />
        <Input
          label="Location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="City, Country"
        />
        <Input
          type="date"
          label="Start Date"
          value={formData.startDate}
          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
        />
        <div className="mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.current}
              onChange={(e) => setFormData({ ...formData, current: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-slate-400">Current position</span>
          </label>
        </div>
        {!formData.current && (
          <Input
            type="date"
            label="End Date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
          />
        )}
        <Textarea
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />
        <Input
          label="Skills Used (comma-separated)"
          value={formData.skillsUsed}
          onChange={(e) => setFormData({ ...formData, skillsUsed: e.target.value })}
          placeholder="React, Node.js, Python"
        />
        <div className="flex gap-4 mt-6">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {editingExp ? 'Update' : 'Add'} Experience
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Experience;

